// Which experience an admin is currently working inside.
//
// An experience is a SCOPE, not a field on a form: pick one and every
// experience-aware screen edits that copy of the config. It used to live in
// each screen's own state, seeded from a ?experience= param that the sidebar
// links never carried — so switching to "Stadium Experience" on Home Buttons
// and clicking through to Side Menu silently put you back on the main app
// config, with nothing on screen saying so. That is a way to edit the wrong
// thing, not just an awkward control.
//
// So the scope lives here instead: one value, owned per admin, surviving
// navigation and reload.
//
//   - persisted in localStorage, keyed by tenant so switching projects cannot
//     inherit a stale id
//   - mirrored into the URL so a link to a screen carries its scope
//   - NEVER written to the database: this is one admin's working context, and
//     one person switching must not move anyone else's ground

import { appDatabasePrimaryFunctions } from '../../base';

// There is no longer a flag for "this club has experiences". The scope is
// meaningful exactly when the club HAS two or more of them, which the data
// answers on its own — a club with none reads back an empty list and every
// path here resolves to '' (the root config) as it always did. The flag only
// ever decided who could see the Experiences screen, and that decision is now
// the feature switch in Feature Access.

const TENANT = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'default';
const STORAGE_KEY = `sqwad.experienceScope.${TENANT}`;

// '' means the default experience, whose config lives at the ROOT nodes
// (settings, homeButtonsBuilt, ...). Only non-default experiences live under
// experiences/{id}/, which is what keeps single-experience tenants untouched.
export function experiencePath(experienceId, node) {
    return experienceId ? `experiences/${experienceId}/${node}` : node;
}

// The screens whose content this scope actually changes. Everything else
// (dashboard, users, rewards, feature access) is tenant-wide, and the chrome
// says so rather than implying the scope is doing something.
export const SCOPED_ROUTES = [
    '/offers',
    '/settings',
    '/homebuttons',
    '/sidemenubuttons',
    '/bottomtabs',
    '/topcarousel',
    '/radio'
];

export function isScopedRoute(pathname) {
    const path = (pathname || '').toLowerCase().replace(/\/+$/, '');
    return SCOPED_ROUTES.indexOf(path) !== -1;
}

function readStored() {
    try {
        return window.localStorage.getItem(STORAGE_KEY) || '';
    } catch (e) {
        return ''; // private browsing, storage disabled — fall back to default
    }
}

function writeStored(id) {
    try {
        if (id) window.localStorage.setItem(STORAGE_KEY, id);
        else window.localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
        /* not fatal — the scope just won't survive a reload */
    }
}

function urlScope() {
    const match = new RegExp('[?&]experience=([^&#]*)').exec(window.location.search);
    return match ? decodeURIComponent(match[1]) : null;
}

// A ?experience= in the URL wins, so a shared link lands on the right scope
// and then becomes this admin's scope.
let current = (() => {
    const fromUrl = urlScope();
    if (fromUrl !== null) {
        writeStored(fromUrl);
        return fromUrl;
    }
    return readStored();
})();

export function getScope() {
    return current;
}

// The sidebar links are plain paths, so a scope restored from storage would
// otherwise be live but invisible in the address bar. Reflect it once on load
// so the URL always says which experience you are in — and stays copyable.
// This runs even when the URL already carries one, because on a tenant-wide
// screen the job is to take it back OUT (see syncUrl).
setTimeout(() => syncUrl(current), 0);

const listeners = new Set();

export function subscribeToScope(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
}

// Keep the address bar honest without reloading the page.
//
// Only where the scope means something. A ?experience= on a tenant-wide
// screen claims a filter that screen does not apply, and next to that screen's
// own team or experience dropdown it reads as the thing driving it — which is
// how an admin ends up unsure which of two controls is selecting what.
function syncUrl(id) {
    try {
        const url = new URL(window.location.href);
        if (id && isScopedRoute(url.pathname)) url.searchParams.set('experience', id);
        else url.searchParams.delete('experience');
        window.history.replaceState({}, '', url);
    } catch (e) {
        /* older browser — the scope still works, the URL just won't show it */
    }
}

export function setScope(id) {
    const next = id || '';
    if (next === current) return;
    current = next;
    writeStored(next);
    syncUrl(next);
    listeners.forEach(fn => {
        try { fn(next); } catch (e) { console.log('Experience scope listener failed:', e); }
    });
}

// A screen with unsaved edits gets to object before the scope changes under
// it. Only one scoped screen is mounted at a time, so a single guard is
// enough; it registers on mount and clears on unmount.
let guard = null;

export function registerScopeGuard(fn) {
    guard = fn;
    return () => { if (guard === fn) guard = null; };
}

export function scopeChangeBlocked() {
    return typeof guard === 'function' ? guard() === false : false;
}

// The experience list, fetched once and shared. Every screen used to fetch it
// separately for its own dropdown.
let cached = null;
let inFlight = null;

export function loadExperiences() {
    if (cached) return Promise.resolve(cached);
    if (inFlight) return inFlight;
    inFlight = appDatabasePrimaryFunctions.ref('experiences').once('value')
        .then(snapshot => {
            const value = snapshot.val() || {};
            cached = Object.entries(value)
                .map(([id, exp]) => ({ id, ...exp }))
                .sort((a, b) => (a.order || 0) - (b.order || 0));
            inFlight = null;
            return cached;
        })
        .catch(e => {
            console.log('Error loading experiences:', e);
            inFlight = null;
            return [];
        });
    return inFlight;
}

// The list is cached for every screen that asks for it, so the ONE screen that
// changes it has to say so. Without this the switcher keeps offering an
// experience that was just deleted and misses one that was just created — most
// visibly right after first-run setup, which creates the second experience that
// makes the switcher appear at all.
export function invalidateExperiences() {
    cached = null;
    inFlight = null;
}

// A scope stored in this browser can outlive the experience it names: another
// admin deletes it, or a rename moves it to a new slug. Left alone the switcher
// prints the dead id as if it were a real experience, and every scoped screen
// then reads AND WRITES experiences/{deadId}/… — recreating it as config no fan
// can reach. Falling back to the default is the only safe reading of a scope
// that no longer resolves.
export function reconcileScope(list) {
    if (!current) return current;
    const stillExists = (list || []).some(exp => exp && exp.id === current);
    if (!stillExists) setScope('');
    return current;
}

export function experienceName(list, id) {
    if (!id) {
        const fallback = (list || []).find(exp => exp && exp.isDefault);
        return fallback ? fallback.name : 'Default';
    }
    const match = (list || []).find(exp => exp.id === id);
    return match ? match.name : id;
}

// The scope only means anything once there is more than one experience to
// choose between — a tenant with one experience sees nothing, as before.
export function scopeIsMeaningful(list) {
    return (list || []).length >= 2;
}

// The id a club uses to name its season ticket members when it has no
// experiences to name them after.
//
// A teamId keys sthRoster, sthRosterEmails, sthRosterAccounts, sthClaims and
// entitlements/{uid}/{teamId}. The SERVER has never cared where it comes from —
// it enumerates sthRoster's own keys and accepts any string. Only four admin
// screens ever insisted it be an experience id, because the experience list was
// the only place they knew how to get a team name from. That left Winterhawks,
// who have fans and could run verification tomorrow, staring at "No teams found
// — create a team experience first" on the one screen that uploads a roster.
export const DEFAULT_TEAM_ID = 'default';

const byOrder = (a, b) => (a.order || 0) - (b.order || 0);

// Accepts either shape the screens already hold: the raw `experiences` object
// read from RTDB, or a list someone has already mapped and sorted.
function asList(experiences) {
    if (Array.isArray(experiences)) return experiences.filter(Boolean);
    return Object.entries(experiences || {}).map(([id, exp]) => ({ id, ...exp }));
}

/**
 * The teams an operator can pick, for rosters, entitlements, members-only
 * offers and members-only pushes.
 *
 * With team experiences, those. Without, ONE synthetic option standing for the
 * club itself — so a club that has never heard of experiences can still say
 * "these are my season ticket members" — carrying `isSynthetic` so a screen can
 * skip the picker entirely rather than offer a choice of one.
 */
export function teamIdOptions(experiences, clubName) {
    const all = asList(experiences);
    const teams = all.filter(exp => exp.type === 'team').sort(byOrder);
    if (teams.length > 0) return teams;
    // Older setups typed nothing at all. Treat every experience as a team there
    // rather than hiding the ones they have behind a synthetic option: a roster
    // uploaded against a real experience id must stay reachable from the
    // screens that grant and target against it. SetUpVerification carried this
    // fallback alone, which is exactly how the four screens came to disagree —
    // it offered the real ids while the other three offered `default`, so a
    // roster and the offers keyed to it pointed at different teams.
    // ...but only where nothing is typed AT ALL. An experience explicitly typed
    // `venue` is a deliberate statement that it is not a team, and Acrisure
    // Arena has no season ticket members of its own — falling back to it would
    // invent a membership programme for a building.
    const nothingIsTyped = all.every(exp => !exp.type);
    if (all.length > 0 && nothingIsTyped) return all.slice().sort(byOrder);
    return [{
        id: DEFAULT_TEAM_ID,
        name: clubName || process.env.REACT_APP_CLEAN_CLIENT_NAME || 'Season ticket members',
        isSynthetic: true
    }];
}
